import React, { useEffect, useState } from 'react'
import QuestionDropDown from '../QuestionDropDown'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MultipleSelectOptions from './MultiSelectOptions'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import { type MultiChoiceQuestion, SurveyItemQuestionType } from '../../../types'
import { useDispatch } from 'react-redux'
import CreateQuestionCardWrapper from '../CreateQuestionCardWrapper'
import { StyledBox, MultiSelectWrapper, StyledOptionWrapper, StyledQuestionWrapper, StyledTextField } from './styles'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import GenericButton from '../../../../../components/GenericButton'
import { addFollowUpFreeResponseQuestionInSection, addSurveyQuestionInSection, type ISurveySection, setSelectedQuestion, updateBranchFilterAnswers, updateBranchFilterPrompt, updateQuestionInSection } from '../../../../../store/slices/SurveyTemplateSlice'
import { getEditableQuestion, getSelectedQuestion } from '../../../../../store/slices/SurveyTemplateSlice/selectors'
import QuestionFooter from '../../QuestionFooter'
import { useTheme } from '@mui/material/styles'
import { grey } from '../../../../../colors'
import { v4 as uuidv4 } from 'uuid'
import { checkQuestionOptionHaveBranching, getUniqueChildBranchesDetails } from '../../../helpers'

interface CreateMultiSelectQuestionCardProps {
  sectionIndex?: number
  questionIndex?: number
  questionNumber?: number
  section: ISurveySection
  isAdminAddedQuestion?: boolean
  isCountComplete?: boolean
}

const CreateMultiSelectQuestionCard: React.FC<CreateMultiSelectQuestionCardProps> = ({ sectionIndex = 0, questionIndex = 0, questionNumber, section, isAdminAddedQuestion, isCountComplete }): JSX.Element => {
  const dispatch = useDispatch()
  const editableQuestion = getEditableQuestion()
  const selectedQuestion = getSelectedQuestion()

  const theme = useTheme()

  const [multiSelectCardData, setMultiSelectCardData] = useState<MultiChoiceQuestion>({
    itemId: uuidv4(),
    itemType: SurveyItemQuestionType.MULTI_SELECT,
    prompt: '',
    object: ['', ''],
    isRequired: false,
    branchingFilters: null
  })

  useEffect(() => {
    if (editableQuestion != null) {
      setMultiSelectCardData(editableQuestion?.question as MultiChoiceQuestion)
      return
    }
    selectedQuestion?.question && setMultiSelectCardData(selectedQuestion?.question as MultiChoiceQuestion)
  }, [editableQuestion, dispatch])

  const handleQuestionTitle = (questionTitle: string): void => {
    // Change Question Prompt in child Branch Section Branching Filters
    if (multiSelectCardData?.branchingFilters) {
      dispatch(updateBranchFilterPrompt({
        childBranches: getUniqueChildBranchesDetails(multiSelectCardData.branchingFilters),
        prompt: questionTitle
      }))
    }

    setMultiSelectCardData(prevState => {
      return { ...prevState, prompt: questionTitle }
    })
  }

  const AddNewOption = (): void => {
    setMultiSelectCardData(prevState => {
      const newOptions = [...prevState?.object]
      newOptions.push('')
      return { ...prevState, object: newOptions }
    })
  }

  const RemoveOption = (index: number): void => {
    // If Question Option have BranchSection  don't Delete show Alert message
    if (checkQuestionOptionHaveBranching(index, multiSelectCardData)) {
      return
    }

    setMultiSelectCardData(prevState => {
      const newOptions = [...prevState.object]
      newOptions.splice(index, 1)
      return { ...prevState, object: newOptions }
    })
  }

  const handleUpdate = (index: number, option: string): void => {
    setMultiSelectCardData(prevState => {
      const newOptions = [...prevState.object]

      if (option !== '') {
        newOptions[index] = option
      } else {
        newOptions[index] = ''
      }

      // If Selected Option have branch section Update that as well
      const oldKey = prevState.object[index]
      const updatedBranchingFilters = prevState.branchingFilters?.map(filter =>
        filter.key === oldKey
          ? { ...filter, key: option }
          : filter
      )
      // Update Child Branch section answers
      const payload = { oldKey, newKey: option, parentQuestionId: prevState.itemId, parentSurveySectionId: section.surveySectionId }
      dispatch(updateBranchFilterAnswers(payload))
      return {
        ...prevState,
        object: newOptions,
        branchingFilters: updatedBranchingFilters ?? multiSelectCardData.branchingFilters
      }
    })
  }

  const handleRequired = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    setMultiSelectCardData(prevState => {
      return { ...prevState, isRequired: checked }
    })
  }

  const handleClickAway = (): void => {
    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multiSelectCardData }))
      return
    }

    dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: multiSelectCardData }))
    dispatch(setSelectedQuestion(null))
  }

  const handleDuplicate = (): void => {
    // Duplicate question without isDirty
    const question = {
      itemId: uuidv4(),
      itemType: SurveyItemQuestionType.MULTI_SELECT,
      prompt: '',
      object: multiSelectCardData.object,
      isRequired: multiSelectCardData.isRequired,
      branchingFilters: null
    }
    if (editableQuestion) {
      // Update the current question in section
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multiSelectCardData }))
      dispatch(setSelectedQuestion({ sectionIndex, questionType: SurveyItemQuestionType.MULTI_SELECT, question }))
    } else {
      // New question: save it first, then set up the duplicate
      dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: multiSelectCardData }))
      dispatch(setSelectedQuestion({ sectionIndex, questionType: SurveyItemQuestionType.MULTI_SELECT, question }))
    }
  }
  const handleAddFollowUp = (): void => {
    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multiSelectCardData }))
      dispatch(addFollowUpFreeResponseQuestionInSection({ surveySectionId: section.surveySectionId, followUpParentQuestionIndex: questionIndex, questionTitle: multiSelectCardData.prompt }))
      dispatch(setSelectedQuestion(null))
    }
  }

  const footerPropsData = {
    questionIndex,
    questionData: multiSelectCardData,
    handleChange: handleRequired,
    handleDuplicate,
    handleClickAway,
    handleAddFollowUp,
    section,
    isAdminAddedQuestion,
    isCountComplete
  }

  const isQuestionHaveBranchFilters = !!multiSelectCardData?.branchingFilters

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleClickAway}>
      <StyledBox >
        <CreateQuestionCardWrapper isAdminAddedQuestion={isAdminAddedQuestion} questionNumber={questionNumber} questionData={multiSelectCardData} section={section}>
          <>
            <StyledQuestionWrapper >
              <StyledTextField value={multiSelectCardData?.prompt} type='text' placeholder='Type your question...' autoFocus onChange={(e) => { handleQuestionTitle(e.target.value) }} />
              <QuestionDropDown section={section} sectionIndex={sectionIndex} questionIndex={questionIndex} isQuestionHaveBranchFilters={isQuestionHaveBranchFilters} />
            </StyledQuestionWrapper>
            <MultiSelectWrapper >
              {multiSelectCardData?.object?.map((option: string, index: number) => (
                <StyledOptionWrapper key={index}>
                  <MultipleSelectOptions option={option} index={index} handleUpdate={handleUpdate} />
                  <GenericButton
                    buttonSize={ButtonSize.Small}
                    buttonType={ButtonType.Ghost}
                    icon={<ClearIcon color='action' />}
                    onClick={() => { RemoveOption(index) }}
                    disabled={multiSelectCardData?.object?.length <= 2} />
                </StyledOptionWrapper>
              ))}
            </MultiSelectWrapper>
            <Box display='flex' gap={theme.spacing(0)} mt={theme.spacing(4)} alignItems='center'>
              <GenericButton
                buttonType={ButtonType.Ghost}
                startIcon={<AddIcon sx={{ fontWeight: 500 }} />}
                onClick={() => { AddNewOption() }}
                label='Add Option'
                buttonSize={ButtonSize.Medium}
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: grey[850],
                  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`
                }}
              />
            </Box>
            <QuestionFooter {...footerPropsData} />
          </>
        </CreateQuestionCardWrapper>
      </StyledBox>
    </ClickAwayListener>
  )
}

export default CreateMultiSelectQuestionCard
